import { engineComponent } from "neo-common";
import { BaseController } from "./BaseController";
import { Symbols } from "../Symbols";
import { WinAnimator } from "../WinAnimator";
import { Machine } from "../Machine";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { GameManager } from "../../Managers/GameManager";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../../Utils/FrameTimer";
import { PlayList } from "../../Components/Sounds/PlayList";
import { ModuleManager } from "../../Managers/ModuleManager";
import { DataManager } from "../../Managers/DataManager";
import { CustomUtils } from "../../Utils/CustomUtils";
import { AmbientBackground } from "../../Utils/AmbientBackground";
import { WinAnimatorExtended } from "../../CustomComponents/WinAnimatorExtended";
import { MachineExtended } from "../../CustomComponents/MachineExtended";
import { PaylinesExtended } from "../../CustomComponents/PaylinesExtended";
import { LayoutManager } from "../../Managers/LayoutManager";
import { LayoutContainerElement } from "../../CustomComponents/LayoutContainerElement";
import { GroupTweens } from "../../CustomComponents/GroupTweens";
import { Symbol } from "../Symbol";

@engineComponent(ComponentTypes.DisplayObject)
export class BaseView extends LayoutContainerElement {
    public static LayoutManager: LayoutManager;

    private _wasFinished: boolean = false;
    private _controller: BaseController;
    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;
    private _machineStoppedDelay: FrameTimerEventEmitter;
    private _allowSkipWildExpandDelay: FrameTimerEventEmitter;
    private _paylines: PaylinesExtended;
    private _symbols: Symbols;
    private _machine: MachineExtended;
    private _winAnimator: WinAnimatorExtended;
    private _groupTweens: SwitchElement;
    private _wildExpanding: boolean = false;

    constructor() {
        super();
        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));
        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));
        this._machineStoppedDelay = CreateFrameTimer("machineStoppedDelay");
        this._machineStoppedDelay.on(FrameTimerEvents.Complete, this.machineStoppedDelayed.bind(this));
        this._allowSkipWildExpandDelay = CreateFrameTimer("expandingDelay");
        this._allowSkipWildExpandDelay.on(FrameTimerEvents.Complete, this.allowSkipWildExpand.bind(this));
    }

    public create(node: Element): void {
        super.create(node);

        BaseView.LayoutManager = new LayoutManager();

        if (this.childs["Symbols"] != null) {
            this._symbols = this.childs["Symbols"];
            this.removeChild(this._symbols);
        }
        if (this.childs["Paylines"] != null) this._paylines = this.childs["Paylines"];
        if (this.childs["Machine"] != null) this._machine = this.childs["Machine"];
        if (this.childs["WinAnimator"] != null) this._winAnimator = this.childs["WinAnimator"];
        if (this.childs["GroupTweens"] != null) this._groupTweens = this.childs["GroupTweens"];

        // set childs to GroupTweens object
        if (this._groupTweens) {
            let groupTweenElements: Object = this._groupTweens.getChildElements();
            if (groupTweenElements) {
                for (let key in groupTweenElements) {
                    if (groupTweenElements.hasOwnProperty(key)) {
                        if (groupTweenElements[key] instanceof GroupTweens) {
                            (<GroupTweens>groupTweenElements[key]).setChilds(this.childs);
                        }
                    }
                }
            }
        }
    }

    public initReceived(controller: BaseController): void {
        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.baseResponse != null) {
                    if (GameManager.baseResponse.initData != null && GameManager.baseResponse.initData.reels != null) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.on(MachineExtended.OnSpinPostRoll, this.onSpinPostRoll.bind(this));
                        this._machine.initialize(
                            this._symbols,
                            GameManager.baseResponse.initData.reels,
                            this._winAnimator,
                        );
                    }

                    if (
                        GameManager.baseResponse.screen != null &&
                        GameManager.baseResponse.screen.reels != null &&
                        GameManager.baseResponse.screen.reels.length !== 0
                    ) {
                        if (this._winAnimator != null) {
                            this._winAnimator.on(
                                WinAnimator.FinishWinCalculation,
                                this.finishWinCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                            this._winAnimator.initialize(
                                this._symbols,
                                this._paylines,
                                this.childs["PaylinesLines"],
                                this.childs["NoWinContainer"],
                                this.childs["WinContainer"],
                                this,
                            );
                            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
                        }
                        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in response");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no base reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }

        this.visible = false;
        if (ModuleManager.gambleActive()) {
            this.visible = true;

            if (this._groupTweens) {
                this._groupTweens.goToCase(GroupTweens.CaseGambleInit);
            }
        }
    }

    public spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    public spin(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.resetVolume("SND_BaseLoop");
        PlayList.loop("SND_BaseLoop");

        AmbientBackground.changeAmbientBackground("base", 30);
        this._machine.spinStart(this._winAnimator.reset(true, true), true, this._winAnimator.currentSymbols);
    }

    public stop(): void {
        if (this._wildExpanding) {
            this.machineStoppedDelayed();
        } else {
            this._machine.spinStop(null, true);
        }
    }

    public collect(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.resetVolume("SND_BaseLoop");
        PlayList.loop("SND_BaseLoop");

        AmbientBackground.changeAmbientBackground("base", 30);
        this._winAnimator.reset(true);
    }

    public initIntro(): void {
        // this.initBaseLoop();

        if (GameManager.gameResponse.totalWin > 0) {
            PlayList.loop("SND_BaseRecapLoop");
        } else {
            PlayList.resetVolume("SND_BaseLoop");
            PlayList.loop("SND_BaseLoop");
        }

        this.visible = true;

        if (this._groupTweens) {
            this._groupTweens.goToCase(GroupTweens.CaseInitIntro);
        }

        this._winAnimator.showInitWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);
    }

    public intro(long: boolean = false): void {
        // this.initBaseLoop();

        this.visible = true;

        if (GameManager.gameResponse.totalWin > 0) {
            // PlayList.setVolume('SND_BaseLoop', 0);
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_BaseRecapLoop");
        } else {
            PlayList.stop("SND_BaseRecapLoop");
            PlayList.resetVolume("SND_BaseLoop");
            PlayList.loop("SND_BaseLoop");

            this.resetWinAnimator();
        }

        if (long) {
            // if (this._groupTweens) {
            //     this._groupTweens.goToCase(GroupTweens.CaseFreespinsToBase);
            // }
            //
            // this._introDelay.start(60);
        } else {
            if (this._groupTweens) {
                this._groupTweens.goToCase(GroupTweens.CaseGambleToBase);
            }
            this._introDelay.start(30);
        }
    }

    private introFinished(): void {
        this._controller.introFinished();
    }

    public outro(): void {
        PlayList.setVolume("SND_BaseLoop", 0);
        PlayList.stop("SND_BaseRecapLoop");

        this.visible = true;

        let wasFinished: boolean = this._wasFinished;
        let inHighWins: boolean = this._winAnimator.reset(true);

        PlayList.play("SND_GambleTransition");
        if (wasFinished) {
            this._outroDelay.start(30);
            if (this._groupTweens) {
                this._groupTweens.goToCase(GroupTweens.CaseBaseToGamble);
            }
        } else {
            if (inHighWins) {
                this._outroDelay.start(90);
                if (this._groupTweens) {
                    this._groupTweens.goToCase(GroupTweens.CaseBaseToGambleHighWins);
                }
            } else {
                this._outroDelay.start(60);
                if (this._groupTweens) {
                    this._groupTweens.goToCase(GroupTweens.CaseBaseToGambleWins);
                }
            }
        }
    }

    private outroFinished(): void {
        this._controller.outroFinished();
        this._winAnimator.resetTimer();
    }

    public disableLogo(): void {
        if (this.childs["Logo"] != null) {
            this.removeChild(this.childs["Logo"]);
        }
    }

    public resetWinAnimator(): void {
        this._winAnimator.reset(true);
    }

    public disableBase(): void {
        this._winAnimator.resetTimer();
    }

    public selectPayline(): void {}

    public toggleSounds(enable: boolean): void {
        if (enable) {
            PlayList.resetVolume("SND_BaseLoop");
            PlayList.loop("SND_BaseLoop");
        } else {
            PlayList.setVolume("SND_BaseLoop", 0);
            PlayList.stop("SND_BaseRecapLoop");
        }
    }

    private initBaseLoop(): void {
        PlayList.resetVolume("SND_BaseLoop");
        PlayList.loop("SND_BaseLoop");
    }

    private onReelStopped(): void {
        this._winAnimator.onReelStopped(GameManager.baseResponse.screen.reels, this._machine.actualReelStopped);
    }

    private machineStopped(): void {
        if (
            this._winAnimator._reelWild.indexOf(0) !== -1 ||
            this._winAnimator._reelWild.indexOf(1) !== -1 ||
            this._winAnimator._reelWild.indexOf(2) !== -1
        ) {
            this._controller.wildExpanding();
            this._winAnimator.expandWild();
            this._wildExpanding = true;
            this._allowSkipWildExpandDelay.start(15);
            this._machineStoppedDelay.start(110);
        } else {
            this.machineStoppedDelayed();
        }
    }

    allowSkipWildExpand(): void {
        this._allowSkipWildExpandDelay.stop();
        this._controller.allowSkipWildExpand();
    }

    machineStoppedDelayed(): void {
        this._machineStoppedDelay.stop();
        this._wasFinished = false;
        this._controller.machineStopped();
        this._wildExpanding = false;
        PlayList.resetVolume("SND_BaseLoop");

        if (this._winAnimator.showWins(GameManager.baseResponse.wins, CustomUtils.getBaseCurrentWin(true))) {
            PlayList.setVolume("SND_BaseLoop", 0);
        }
    }

    private finishWinCalculation(): void {
        this._wasFinished = true;
        this._controller.winCalculationFinished();
    }

    private finishWinAnimator(afterBonusGame: boolean = false): void {
        if (!DataManager.Autoplay) {
            PlayList.setVolume("SND_BaseLoop", 0);
        }

        this._controller.allWinsFinished();
    }

    private onSpinPostRoll(impactSymbols: Array<Symbol>, reelIndex: number): void {
        if (impactSymbols) {
            this._winAnimator.onSpinPostRoll(impactSymbols, reelIndex);
        }
        if (reelIndex === this._winAnimator.getReelCount() - 1) {
            this._controller.rollFinished();
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Base/BaseView.ts
 **/